/**
 * RowHistoryModal — full change history for one Nut Sheet row.
 *
 * Queries the audit log by resourceId (`{locationId}#{date}#{entryId}`) and
 * renders each create/update/delete newest-first with a field-level old→new
 * diff. This is the defensible record for a commission dispute: who changed
 * which number on a deal, and when.
 */

import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AuditChange, AuditLogEntry } from '../../types/audit';
import {
  getAuditLogs,
  formatAuditAction,
  formatFieldName,
  formatNutSheetValue,
} from '../../services/auditApi';

interface RowHistoryModalProps {
  locationId: number;
  date: string;
  entryId: string;
  onClose: () => void;
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  });
}

function actionBadgeClass(action: string): string {
  switch (action) {
    case 'NUT_SHEET_CREATE':
      return 'bg-green-100 text-green-800';
    case 'NUT_SHEET_UPDATE':
      return 'bg-blue-100 text-blue-800';
    case 'NUT_SHEET_DELETE':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export const RowHistoryModal: React.FC<RowHistoryModalProps> = ({
  locationId,
  date,
  entryId,
  onClose,
}) => {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await getAuditLogs({ resourceId: `${locationId}#${date}#${entryId}` });
      if (cancelled) return;
      if (res.success && res.items) {
        setEntries(res.items);
      } else {
        setError(res.error || 'Failed to load history');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId, date, entryId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Row change history</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          {loading && <div className="text-sm text-gray-500">Loading history…</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div className="text-sm text-gray-500 italic">No history recorded for this row yet.</div>
          )}

          {entries.map((entry) => {
            const changes: AuditChange[] = entry.details?.changes || [];
            return (
              <div key={entry.auditId} className="border border-gray-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full ${actionBadgeClass(entry.action)}`}
                  >
                    {formatAuditAction(entry.action)}
                  </span>
                  <span className="text-xs text-gray-500">{formatTimestamp(entry.timestamp)}</span>
                </div>
                <div className="text-xs text-gray-700 mb-2">
                  <span className="font-medium">{entry.userDisplayName}</span>
                  <span className="text-gray-400"> · {entry.userEmail}</span>
                </div>
                {changes.length > 0 ? (
                  <div className="space-y-1">
                    {changes.map((change, idx) => (
                      <div key={idx} className="text-xs flex flex-wrap items-center gap-1">
                        <span className="font-medium text-gray-700">
                          {formatFieldName(change.field)}:
                        </span>
                        <span className="text-red-600 line-through">
                          {formatNutSheetValue(change.field, change.oldValue)}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="text-green-700 font-medium">
                          {formatNutSheetValue(change.field, change.newValue)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 italic">No field changes recorded.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
